import React, { useEffect, useState } from 'react';
import { getReadTimeFromChars } from '../../../utils/read-time';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Box,
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  useToast
} from '@chakra-ui/react';
import { MoreHorizontal } from 'lucide-react';
import { useAtom } from 'jotai';

import LexicalArticleViewer from '../../../components/Lexical/LexicalArticleViewer';
import AwardWriterModal from '../../../components/AwardWriterModal';
import InvestArticleModal from '../../../components/InvestArticleModal';
import ShareContentModal from '../../../components/ShareContentModal';
import InvestmentButton from '../../ArticleViewer/components/ArticleHeader/investment-button.component';
import ArticleReactionStatsModal from '../../ArticleViewer/components/ArticleHeader/stats-modal.component';
import ReportSidePanel from '../../ArticleViewer/components/GeneralComment/components/ReportSidePanel';

import { fetchArticleDetail, createReputation, investArticle } from '../../../apis/article.api';
import { useBookmarkArticleMutation } from '../../../apis/queries/useBookmarkArticleMutation';
import { useIgnoreArticleMutation } from '../../../apis/queries/useIgnoreArticleMutation';
import { useFollowUserMutation } from '../../../apis/queries/useFollowUserMutation';
import { profileAtom } from '../../../components/Settings/atom';
import { convertCountLike } from '../../../constants';
import { UnauthorizedDispatcher } from '../../../utils/unauthorized-dispatcher';
import DataLayer from '../../../utils/data-layer';
import ToastBtnMessage from '../../../components/ToastBtnMessage';
import { ResourceType } from '../../../types/moderation.types';
import { type CommunityPerspective } from './BiasSpaceMap';

import ReactStackIcon from '../../../assets/react-stack.svg';
import CommentIcon from '../../../assets/comment-article-icon.svg';
import EyeDarkIcon from '../../../assets/eye-dark-icon.svg';
import ShareIcon from '../../../assets/share-gray-icon.svg';
import SavedIcon from '../../../assets/saved-gray-icon.svg';
import SaveIcon from '../../../assets/save-gray-icon.svg';
import IgnoreIcon from '../../../assets/ignore-gray-icon.svg';
import UnignoreIcon from '../../../assets/ignore-green-icon.svg';
import FlagIcon from '../../../assets/flag.svg';
import CommentArticleIcon from '../../../assets/comment-article-icon.svg';
import PointIcon from '../../../assets/point-icon.svg';
import PointBoldIcon from '../../../assets/point-bold-icon.svg';
import CheckedIcon from '../../../assets/checked-icon.svg';
import PlusGreenIcon from '../../../assets/plus-bold-icon.svg';

const LeafIcon = ({ size = 12 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17 8C8 10 5.9 16.17 3.82 21.34l1.89.66.95-2.3c.48.17.98.3 1.34.3C19 20 22 3 22 3c-1 2-8 2.25-13 3.25S2 11.5 2 13.5s1.75 3.75 1.75 3.75" />
  </svg>
);

interface CommunityArticleContentProps {
  articleId: string;
  perspective: CommunityPerspective;
}

const CommunityArticleContent: React.FC<CommunityArticleContentProps> = ({
  articleId,
  perspective
}) => {
  const navigate = useNavigate();
  const toast = useToast();
  const [profile, setProfile] = useAtom(profileAtom);
  const [userId] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}')?.id;
    } catch {
      return undefined;
    }
  });

  const {
    data: fetchedArticle,
    isLoading,
    refetch
  } = useQuery({
    queryKey: ['article', articleId],
    queryFn: () => fetchArticleDetail(articleId),
    enabled: !!articleId,
    staleTime: 60_000
  });

  const [article, setArticle] = useState<any>(null);
  useEffect(() => {
    if (fetchedArticle) setArticle(fetchedArticle);
  }, [fetchedArticle]);

  const [modal, setModal] = useState('');
  const [openReportModal, setOpenReportModal] = useState(false);
  const [reportType, setReportType] = useState('');

  const { mutate: saveArticle } = useBookmarkArticleMutation();
  const { mutate: ignoreArticle } = useIgnoreArticleMutation();
  const { mutateAsync: followUser, isPending: isFollowingUser } = useFollowUserMutation(articleId);
  const { mutateAsync: createPoint, isPending: isCreatingPoint } = useMutation({
    mutationFn: createReputation
  });
  const { mutateAsync: investPointArticle, isPending: isInvesting } = useMutation({
    mutationFn: investArticle
  });

  const handleSaveArticle = () => {
    if (!userId) return UnauthorizedDispatcher('required');
    saveArticle({ articleId: article?.id, isSaved: article?.isSaved, article });
  };

  const handleIgnoreArticle = () => {
    if (!userId) return UnauthorizedDispatcher('required');
    ignoreArticle({
      articleId: article?.id,
      isIgnored: article?.isIgnored,
      toastIgnore: undefined
    });
  };

  const handleScrollToComments = () => {
    const el = document.getElementById('general-comment');
    if (el) {
      const pos = el.getBoundingClientRect().top + window.scrollY - 92;
      window.scrollTo({ top: pos, behavior: 'smooth' });
    }
  };

  const handleCreatePoint = async (point: number) => {
    try {
      const result: any = await createPoint({
        amount: point,
        recipientId: article?.creatorId,
        entityId: article?.id,
        entityType: 'article'
      });
      toast({
        render: () => (
          <ToastBtnMessage
            title="Successfully awarded the article!"
            isError={false}
            isHiddenButton
          />
        ),
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right'
      });
      if (article?.id) DataLayer.trackArticleAward(article.writingType?.name, article.id);
      setProfile({ ...profile, dailyPointsRemaining: result?.dailyPointsRemaining });
      setModal('');
      refetch();
    } catch (error: any) {
      toast({
        render: () => (
          <ToastBtnMessage title={error?.response?.data?.message} isError isHiddenButton />
        ),
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right'
      });
    }
  };

  const handleInvestPoints = async (point: number) => {
    try {
      await investPointArticle({ articleId: article?.id, pointInvested: point });
      setProfile({ ...profile, totalRawPointsRemaining: profile.totalRawPointsRemaining - point });
      toast({
        render: () => (
          <ToastBtnMessage
            title="Thanks for investing in this article! Your support helps boost great content!"
            isError={false}
            btnTitle="View Investments"
            btnAction={() => navigate('/article/manage?tab=investments')}
          />
        ),
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top-right'
      });
      refetch();
    } catch (error: any) {
      toast({
        render: () => (
          <ToastBtnMessage title={error?.response?.data?.message} isError isHiddenButton />
        ),
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right'
      });
    } finally {
      setModal('');
    }
  };

  const handleFollowUser = async () => {
    try {
      if (!userId) return UnauthorizedDispatcher('required');
      await followUser({
        userId: article?.creator?.id,
        isFollowing: article?.creator?.isFollowing,
        writerUsername: article?.creator?.username
      });
    } catch (error) {
      console.error(error);
    }
  };

  if (isLoading || !article) {
    return (
      <Box display="flex" justifyContent="center" py={16}>
        <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="green.500" size="xl" />
      </Box>
    );
  }

  const initials = (perspective.authorName || '??')
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const dateStr = article.publishedAt
    ? new Date(article.publishedAt).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    : '';

  const totalReactions = Object.values(article?.reactions || {}).reduce(
    (sum: number, count) => sum + (count as number),
    0
  );

  const isOwnArticle = !!(article?.creatorId && profile?.id && article.creatorId === profile.id);

  return (
    <div>
      {/* Title */}
      <h1
        className="text-[32px] font-extrabold text-slate-900 leading-tight mb-3"
        style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}
      >
        {article.title}
      </h1>

      {article.subTitle && (
        <p
          className="text-lg text-slate-500 leading-relaxed mb-7"
          style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}
        >
          {article.subTitle}
        </p>
      )}

      {/* Author byline */}
      <div className="flex items-center gap-3 pb-5 border-b border-slate-200 mb-5">
        <div className="relative shrink-0">
          <div
            className="w-11 h-11 rounded-full flex items-center justify-center text-white text-sm font-bold"
            style={{ background: '#059669', fontFamily: "'JetBrains Mono', monospace" }}
          >
            {initials}
          </div>
          <div
            className="absolute font-extrabold rounded border-2 border-white"
            style={{
              bottom: '-2px',
              right: '-4px',
              background: '#059669',
              color: '#ffffff',
              fontSize: '7px',
              padding: '2px 4px',
              fontFamily: "'JetBrains Mono', monospace",
              letterSpacing: '0.05em'
            }}
          >
            ✦
          </div>
        </div>
        <div>
          <div className="text-[15px] font-semibold text-slate-900">
            {perspective.authorName}
            {perspective.authorUsername && (
              <span
                className="ml-2 text-[11px] px-2 py-0.5 rounded font-bold"
                style={{
                  background: 'rgba(5,150,105,0.08)',
                  color: '#059669',
                  fontFamily: "'JetBrains Mono', monospace"
                }}
              >
                @{perspective.authorUsername}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-0.5 text-[12px]">
            <span
              className="flex items-center gap-1 font-semibold"
              style={{ color: '#059669', fontFamily: "'JetBrains Mono', monospace" }}
            >
              <LeafIcon size={12} />
              {perspective.twygCount ?? 0} twygs
            </span>
            {dateStr && <span className="text-slate-400">{dateStr}</span>}
            {article.contentLength ? (
              <span className="text-slate-400">{getReadTimeFromChars(article.contentLength)}</span>
            ) : null}
          </div>
        </div>
      </div>

      {/* Stats + action buttons */}
      <div className="flex flex-col gap-3 mb-7">
        <div className="flex items-center justify-between">
          {/* Stats */}
          <div className="flex items-center gap-3">
            <div
              className="flex items-center gap-1.5 cursor-pointer hover:opacity-80"
              onClick={() => setModal('reaction-modal')}
            >
              <img className="h-5" src={ReactStackIcon} alt="" />
              <span className="text-sm font-semibold">{convertCountLike(totalReactions)}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <img src={CommentIcon} alt="" className="w-5 h-5" />
              <span className="text-sm font-semibold">{article?.commentNumber || 0}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <img src={EyeDarkIcon} alt="" className="w-5 h-5" />
              <span className="text-sm font-semibold">{article?.viewNumber || 0}</span>
            </div>
          </div>

          {/* Share / Bookmark / Menu */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => setModal('share-article')}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors flex items-center justify-center"
              style={{ minWidth: '36px', minHeight: '36px' }}
            >
              <img src={ShareIcon} alt="Share" className="w-5 h-5" />
            </button>
            <button
              onClick={handleSaveArticle}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors flex items-center justify-center"
              style={{ minWidth: '36px', minHeight: '36px' }}
            >
              <img
                src={article?.isSaved ? SavedIcon : SaveIcon}
                alt={article?.isSaved ? 'Saved' : 'Save'}
                className="w-5 h-5"
              />
            </button>
            <Menu>
              <MenuButton
                as={Button}
                variant="unstyled"
                border="none"
                boxShadow="none"
                h="36px"
                w="36px"
                rounded="full"
                _hover={{ bg: '#F0F0F0' }}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <MoreHorizontal className="w-5 h-5" />
              </MenuButton>
              <MenuList minW="unset" p="0.625rem" shadow="md" rounded="12px">
                <MenuItem
                  _hover={{ bgColor: 'anti-flash-white' }}
                  rounded="0.5rem"
                  py="8px"
                  px="10px"
                  fontSize="sm"
                  fontWeight={900}
                  _focus={{ bgColor: 'anti-flash-white' }}
                  icon={<img src={CommentArticleIcon} alt="" className="w-5 h-5" />}
                  onClick={handleScrollToComments}
                >
                  Comment on Article
                </MenuItem>
                {!isOwnArticle && (
                  <MenuItem
                    _hover={{ bgColor: 'anti-flash-white' }}
                    rounded="0.5rem"
                    py="8px"
                    px="10px"
                    fontSize="sm"
                    fontWeight={900}
                    _focus={{ bgColor: 'anti-flash-white' }}
                    icon={<img src={FlagIcon} alt="" className="w-5 h-5" />}
                    onClick={() => {
                      setOpenReportModal(true);
                      setReportType(ResourceType.ARTICLE);
                    }}
                  >
                    Report Article
                  </MenuItem>
                )}
                {!isOwnArticle && (
                  <MenuItem
                    _hover={{ bgColor: 'anti-flash-white' }}
                    rounded="0.5rem"
                    py="8px"
                    px="10px"
                    fontSize="sm"
                    fontWeight={900}
                    _focus={{ bgColor: 'anti-flash-white' }}
                    icon={
                      <img
                        src={article?.isIgnored ? UnignoreIcon : IgnoreIcon}
                        alt=""
                        className="w-5 h-5"
                      />
                    }
                    onClick={handleIgnoreArticle}
                  >
                    Ignore
                  </MenuItem>
                )}
              </MenuList>
            </Menu>
          </div>
        </div>

        {/* Award + Invest */}
        {!isOwnArticle && (
          <div className="flex items-center gap-2 flex-wrap">
            <div
              className={`flex gap-1 items-center py-1 px-2 border ${article?.reputationPoint?.given ? 'border-[#87C98A] bg-growth-green-200' : 'border-[#C8E6C9] bg-06-seasalt-white'} rounded-[80px] w-fit cursor-pointer hover:opacity-80`}
              onClick={() => setModal('award-writer')}
            >
              <img
                src={article?.reputationPoint?.given ? PointBoldIcon : PointIcon}
                className="w-4 h-4 md:w-6 md:h-6"
                alt=""
              />
              {article?.reputationPoint?.amount > 0 ? (
                <p className="text-growth-green-900 font-[800] text-xs md:text-sm">
                  {article.reputationPoint.amount}{' '}
                  {article.reputationPoint.amount === 1 ? 'Twig' : 'Twigs'}{' '}
                  <span className="font-[600]">
                    · {article.reputationPoint.count || 0}{' '}
                    {article.reputationPoint.count === 1 ? 'Reader' : 'Readers'}
                  </span>
                </p>
              ) : (
                <span className="text-xs md:text-sm font-[800] text-growth-green-900">Award</span>
              )}
              {article?.reputationPoint?.given ? (
                <img src={CheckedIcon} alt="" className="w-4 h-4 md:w-5 md:h-5" />
              ) : (
                <img src={PlusGreenIcon} alt="" className="w-4 h-4 md:w-5 md:h-5" />
              )}
            </div>
            <InvestmentButton article={article} setModal={setModal} />
          </div>
        )}
      </div>

      {/* Lexical content */}
      <LexicalArticleViewer
        key={article.id}
        showSensitiveContentCard={false}
        content={article.content}
        articleId={article.id}
        article={article}
      />

      {/* Modals */}
      {modal === 'award-writer' && (
        <AwardWriterModal
          isOpen
          handleCreatePoint={handleCreatePoint}
          previousPoint={article?.reputationPoint?.given}
          title={`Recognize ${article?.creator?.username || article?.creator?.firstName}'s article`}
          onClose={() => setModal('')}
          isLoading={isCreatingPoint}
          alreadyGivePoint={article?.reputationPoint?.given !== 0}
          dailyLimits={article?.reputationPoint?.dailyLimits}
          contentType="article"
        />
      )}
      {modal === 'invest-article' && (
        <InvestArticleModal
          article={article}
          setOpenLearnMorePanel={() => {}}
          handleInvestArticle={handleInvestPoints}
          handleFollowUser={handleFollowUser}
          isFollowingUser={isFollowingUser}
          initialPoints={article?.investmentData?.pointInvested}
          isInvesting={isInvesting}
          alreadyInvested={!!article?.investmentData?.pointInvested}
          isOpen
          onClose={() => setModal('')}
        />
      )}
      {modal === 'share-article' && (
        <ShareContentModal
          isOpen
          title="Share Article"
          smallTitle="Share this Article!"
          shareUrl={`${process.env.REACT_APP_NEWSLETTER_URL}articles/${article?.alternativeId || article?.id}`}
          media={article?.cover}
          articleId={article?.alternativeId || article?.id}
          article={article}
          onClick={() => setModal('')}
          titleBtn="Done"
          minW="439px"
        />
      )}
      {modal === 'reaction-modal' && (
        <ArticleReactionStatsModal
          isOpen
          onClose={() => setModal('')}
          reactions={article.reactions}
          totalComment={article.totalCommentCount}
          articleId={article.id}
        />
      )}
      {openReportModal && (
        <ReportSidePanel
          article={article}
          targetType={reportType}
          openReportModal={openReportModal}
          setOpenReportModal={setOpenReportModal}
          handleIgnoreArticle={handleIgnoreArticle}
        />
      )}
    </div>
  );
};

export default CommunityArticleContent;
